import { useQuery } from "react-query";
import {
	fetchCenter,
	fetchCenterAudios,
	fetchCenterCommands,
	fetchCenterCommandsEarmoldTypesStats,
	fetchCenterCommandsStats,
	fetchCenterCommandsStatusStats,
	fetchCenterPatients,
	fetchCenterPatientsStats,
	fetchCenters,
	fetchCenterStat,
	fetchCenterTicketsTypesStats,
	fetchCenterUsers,
	fetchCreationRequestCenters,
} from "../queries/center.queries";
import { PaginatedFilter } from "../types/axios.types";
import { useCentersStore } from "../stores/centers.store";
import { StatsFilterProps } from "../types/stats.types";

export const useCenters = () => {
	const { center, centers, setCurrentCenter, setCenters } = useCentersStore();
	useQuery(["centers"], async () => {
		try {
			const paginated = await fetchCenters(1, 1000);
			const centers = paginated.data;
			if (center === null && centers?.length > 0) {
				setCurrentCenter(centers[0]);
			}
			if (center !== null && centers?.length === 0) {
				setCurrentCenter(null);
			}
			setCenters(centers);
			return centers;
		} catch (err) {
			return null;
		}
	});
	return { centers, center, setCurrentCenter, setCenters };
};

export const useCentersPaginated = (
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
	isArchived?: boolean,
) => {
	const query = useQuery(
		["centers", page, size, search, filters, sortBy, isArchived],
		() => {
			try {
				return fetchCenters(page, size, search, filters, sortBy, isArchived);
			} catch (err) {
				return null;
			}
		},
		{ keepPreviousData: true },
	);
	return query.data;
};

export const useCreationRequestCenters = () => {
	return useQuery(["creation-request-centers"], () => {
		try {
			return fetchCreationRequestCenters();
		} catch (err) {
			return null;
		}
	});
};

export const useCenter = (id: number | string | undefined) => {
	return useQuery(["center", id], () => {
		try {
			return fetchCenter(id);
		} catch (err) {
			return null;
		}
	});
};

export const useCenterStat = (id: number | string | undefined) => {
	return useQuery(["center-stats", id], () => {
		try {
			return fetchCenterStat(id);
		} catch (err) {
			return null;
		}
	});
};

export const useCenterUsers = (id: number | string | undefined) => {
	return useQuery(["center-users", id], () => {
		try {
			return fetchCenterUsers(id);
		} catch (err) {
			return null;
		}
	});
};

export const useCenterAudio = (id: number | string | undefined) => {
	return useQuery(["center-audios", id], () => {
		try {
			return fetchCenterAudios(id, 1, 1000);
		} catch (err) {
			return null;
		}
	});
};

export const useCenterUsersPaginated = ({
	id,
}: {
	id: number | string | undefined;
}) => {
	return (
		page?: number,
		size?: number,
		search?: string,
		filters?: PaginatedFilter,
		sortBy?: string[],
	) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const query = useQuery(
			["center-users-paginated", id, page, size, search, filters, sortBy],
			() => {
				try {
					return fetchCenterUsers(id, page, size, search, filters, sortBy);
				} catch (err) {
					return null;
				}
			},
			{ keepPreviousData: true },
		);
		return query?.data;
	};
};

export const useCenterPatientsPaginated = ({
	id,
}: {
	id: number | string | undefined;
}) => {
	return (
		page?: number,
		size?: number,
		search?: string,
		filters?: PaginatedFilter,
		sortBy?: string[],
	) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const query = useQuery(
			["center-patients-paginated", id, page, size, search, filters, sortBy],
			() => {
				try {
					return fetchCenterPatients(id, page, size, search, filters, sortBy);
				} catch (err) {
					return null;
				}
			},
			{ keepPreviousData: true },
		);
		return query?.data;
	};
};

export const useCenterCommandsPaginated = ({
	id,
	filters: prefilters,
}: {
	id: number | string | undefined;
	filters?: PaginatedFilter;
}) => {
	return (
		page?: number,
		size?: number,
		search?: string,
		filters?: PaginatedFilter,
		sortBy?: string[],
	) => {
		const computedFilters = { ...prefilters, ...filters };
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const query = useQuery(
			["commands-paginated", id, page, size, search, computedFilters, sortBy],
			() => {
				try {
					return fetchCenterCommands(
						id,
						page,
						size,
						search,
						computedFilters,
						sortBy,
					);
				} catch (err) {
					return null;
				}
			},
			{ keepPreviousData: true },
		);
		return query?.data;
	};
};

export const useCenterCommandsStats = ({
	id,
}: {
	id: string | number | undefined;
}) => {
	return useQuery({
		queryKey: ["center", "stats", "commands", id],
		queryFn: () => {
			return fetchCenterCommandsStats({ id });
		},
	});
};

export const useCenterPatientsStats = ({
	id,
}: {
	id: string | number | undefined;
}) => {
	return useQuery({
		queryKey: ["center", "stats", "patients", id],
		queryFn: () => {
			return fetchCenterPatientsStats({ id });
		},
	});
};

export const useCenterCommandsStatusStats = ({
	id,
	date,
}: { id: string | number | undefined } & StatsFilterProps) => {
	return useQuery({
		queryKey: ["center", "stats", "patients", id, date],
		queryFn: () => {
			return fetchCenterCommandsStatusStats({ id, date });
		},
	});
};

export const useCenterCommandsEarmoldTypesStats = ({
	id,
	date,
}: { id: string | number | undefined } & StatsFilterProps) => {
	return useQuery({
		queryKey: ["center", "stats", "earmold", id, date],
		queryFn: () => {
			return fetchCenterCommandsEarmoldTypesStats({ id, date });
		},
	});
};

export const useCenterTicketsTypesStats = ({
	id,
	date,
}: { id: string | number | undefined } & StatsFilterProps) => {
	return useQuery({
		queryKey: ["center", "stats", "tickets", "types", id, date],
		queryFn: () => {
			return fetchCenterTicketsTypesStats({ id, date });
		},
	});
};
